import { z } from "zod";
import { PLATFORM_MODULES } from "./modules.js";

const integrationMode = z.enum(["FAKE", "SANDBOX", "PRODUCTION"]);

const tenantConfigBaseSchema = z.object({
  schemaVersion: z.literal(1),
  slug: z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
  product: z.object({
    code: z.literal("YATTA_GESTOR_NEGOCIOS"),
    planCode: z.string().min(1),
    verticalTemplateCode: z.string().min(1),
    releaseChannel: z.enum(["stable", "pilot", "beta"]).default("pilot"),
    commercialProductCode: z.string().min(1).optional(),
    commercialProductName: z.string().min(2).optional(),
  }),
  organization: z.object({
    legalName: z.string().min(3),
    tradeName: z.string().min(2),
    taxId: z.string().optional(),
    timezone: z.string().default("America/Sao_Paulo"),
    locale: z.string().default("pt-BR"),
    currency: z.literal("BRL").default("BRL"),
  }),
  units: z
    .array(
      z.object({
        code: z.string().min(1),
        name: z.string().min(2),
        timezone: z.string().default("America/Sao_Paulo"),
      }),
    )
    .min(1),
  branding: z.object({
    productName: z.string().min(2),
    shortName: z.string().min(2),
    logoUrl: z.string().url().optional(),
    faviconUrl: z.string().url().optional(),
    primaryColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/),
    secondaryColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/),
    accentColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/),
    backgroundColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/),
    supportEmail: z.string().email().optional(),
    supportPhone: z.string().optional(),
  }),
  domains: z
    .array(
      z.object({
        hostname: z.string().min(3),
        kind: z.enum(["APP", "PORTAL", "ONBOARDING", "PAYMENT_PORTAL"]),
        primary: z.boolean().default(false),
      }),
    )
    .default([]),
  modules: z.array(z.enum(PLATFORM_MODULES)).min(1),
  integrations: z.object({
    eva: z.object({ mode: integrationMode, enabled: z.boolean() }),
    paglog: z.object({ mode: integrationMode, enabled: z.boolean() }),
    mobimedical: z.object({ mode: integrationMode, enabled: z.boolean() }),
    signature: z.object({ mode: integrationMode, enabled: z.boolean() }),
  }),
  publicPaymentPortal: z
    .object({
      enabled: z.boolean().default(false),
      requireOtp: z.boolean().default(true),
      lookupIdentifiers: z.array(z.enum(["CPF", "CNPJ"])).default(["CPF", "CNPJ"]),
      allowedMethods: z.array(z.enum(["PIX", "BOLETO"])).default(["PIX", "BOLETO"]),
      tokenMinutes: z.number().int().min(1).max(60).default(15),
      maxAttempts: z.number().int().min(1).max(10).default(5),
    })
    .optional(),
  businessConfig: z.object({
    productionSalesBlocked: z.boolean(),
    pricingApproved: z.boolean(),
    splitApproved: z.boolean(),
    commissionApproved: z.boolean(),
    legalDocumentsApproved: z.boolean(),
    migrationApproved: z.boolean(),
    notes: z.array(z.string()).default([]),
  }),
  initialAdmin: z
    .object({
      email: z.string().email(),
      fullName: z.string().min(3),
    })
    .optional(),
  metadata: z.record(z.string(), z.unknown()).default({}),
});

export const tenantConfigSchema = tenantConfigBaseSchema.superRefine((config, context) => {
  if (config.publicPaymentPortal?.enabled) {
    if (!config.publicPaymentPortal.requireOtp) {
      context.addIssue({
        code: "custom",
        path: ["publicPaymentPortal", "requireOtp"],
        message: "O portal público deve exigir OTP.",
      });
    }
    if (!config.modules.includes("public_payment_portal")) {
      context.addIssue({
        code: "custom",
        path: ["modules"],
        message: "O módulo public_payment_portal é obrigatório quando o portal está habilitado.",
      });
    }
    if (!config.integrations.paglog.enabled) {
      context.addIssue({
        code: "custom",
        path: ["integrations", "paglog", "enabled"],
        message: "Paglog deve estar habilitada para emitir segunda via.",
      });
    }
  }
});

export type TenantConfig = z.infer<typeof tenantConfigSchema>;

export function parseTenantConfig(input: unknown): TenantConfig {
  return tenantConfigSchema.parse(input);
}
